import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

// Icons
const BackIcon = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);
const ExpandIcon = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>);
const PlayIcon = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="white"><polygon points="6,3 20,12 6,21"/></svg>);
const PauseIcon = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="white"><rect x="5" y="3" width="5" height="18" rx="1.5"/><rect x="14" y="3" width="5" height="18" rx="1.5"/></svg>);
const StopIcon = () => (<svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>);
const PencilIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);

const TIME_SIGS = ['2/4','3/4','4/4','5/4','6/8','7/8'];
const SUBDIVISIONS = [
  { label: 'Quarter notes', value: 'quarter', symbol: '♩' },
  { label: 'Eighth notes', value: 'eighth', symbol: '♪' },
  { label: 'Triplets', value: 'triplet', symbol: '♩₃' },
  { label: 'Sixteenth notes', value: 'sixteenth', symbol: '𝅘𝅥𝅯' },
];

function useMetronome(bpm, timeSig, subdivision, isPlaying, rhythmTrainer, soundBars, silentBars, rhythmBarCountRef, isSilentRef) {
  const audioCtx = useRef(null);
  const nextBeat = useRef(0);
  const beatCount = useRef(0);
  const scheduler = useRef(null);
  const [currentBeat, setCurrentBeat] = useState(-1);

  const beatsPerMeasure = parseInt(timeSig.split('/')[0]);
  // How many subdivisions per beat
  const subMult = subdivision === 'eighth' ? 2 : subdivision === 'triplet' ? 3 : subdivision === 'sixteenth' ? 4 : 1;
  // Total clicks per measure
  const totalPerMeasure = beatsPerMeasure * subMult;

  const getInterval = useCallback(() => {
    let base = 60 / bpm;
    if (subdivision === 'eighth') base /= 2;
    if (subdivision === 'triplet') base /= 3;
    if (subdivision === 'sixteenth') base /= 4;
    return base;
  }, [bpm, subdivision]);

  const scheduleClick = useCallback((time, isAccent, isBeat) => {
    if (!audioCtx.current) return;
    const ctx = audioCtx.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    // Accent = beat 1, beat = other main beats, sub = subdivisions
    osc.frequency.value = isAccent ? 1300 : isBeat ? 1000 : 800;
    gain.gain.setValueAtTime(isAccent ? 0.55 : isBeat ? 0.35 : 0.15, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.045);
    osc.start(time); osc.stop(time + 0.05);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      if (ctx.state === 'suspended') ctx.resume();
      nextBeat.current = ctx.currentTime + 0.05;
      beatCount.current = 0;
      if (rhythmTrainer) { rhythmBarCountRef.current = 0; isSilentRef.current = false; }
      const tick = () => {
        while (nextBeat.current < ctx.currentTime + 0.15) {
          const clickIndex = beatCount.current % totalPerMeasure;
          const isBeat = clickIndex % subMult === 0;           // is this a main beat?
          const beatIndex = Math.floor(clickIndex / subMult);  // which beat (0,1,2,3...)
          const isAccent = clickIndex === 0;                   // beat 1 of measure

          // Rhythm trainer — update silence state at the START of each measure
          if (rhythmTrainer && clickIndex === 0) {
            const totalCycle = soundBars + silentBars;
            const posInCycle = rhythmBarCountRef.current % totalCycle;
            isSilentRef.current = posInCycle >= soundBars;
            rhythmBarCountRef.current += 1;
          }

          // Mute during silent measures
          const shouldPlay = !rhythmTrainer || !isSilentRef.current;
          if (shouldPlay) scheduleClick(nextBeat.current, isAccent, isBeat);

          // Only update the dot display on main beats
          if (isBeat) {
            const t = nextBeat.current;
            setTimeout(() => setCurrentBeat(beatIndex), Math.max(0, (t - ctx.currentTime) * 1000 - 20));
          }

          beatCount.current++;
          nextBeat.current += getInterval();
        }
        scheduler.current = setTimeout(tick, 25);
      };
      tick();
    } else {
      clearTimeout(scheduler.current);
      setCurrentBeat(-1);
    }
    return () => clearTimeout(scheduler.current);
  }, [isPlaying, bpm, timeSig, subdivision, getInterval, scheduleClick, totalPerMeasure, subMult, rhythmTrainer, soundBars, silentBars]);

  return { currentBeat, isSilentNow: rhythmTrainer && isSilentRef.current };
}

export default function ExerciseScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const programSettings = location.state?.programSettings || null;
  const defaultTimeSig = location.state?.defaultTimeSig || '4/4';

  const [exercise, setExercise] = useState(null);
  const [logs, setLogs] = useState([]);
  const [note, setNote] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const [editingNote, setEditingNote] = useState(false);
  const [imgExpanded, setImgExpanded] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finished, setFinished] = useState(false);

  // Metro
  const [bpm, setBpm] = useState(80);
  const [timeSig, setTimeSig] = useState(defaultTimeSig);
  const [subdivision, setSubdivision] = useState('quarter');
  const [metroOn, setMetroOn] = useState(false);

  // Rhythm Trainer
  const [rhythmTrainer, setRhythmTrainer] = useState(false);
  const [soundBars, setSoundBars] = useState(2);   // compassos a tocar
  const [silentBars, setSilentBars] = useState(2); // compassos de silêncio
  const rhythmBarCount = useRef(0); // current measure count
  const isSilent = useRef(false);   // currently silent?

  // Note modal before saving
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [pendingDuration, setPendingDuration] = useState(0);
  const [sessionNote, setSessionNote] = useState('');

  // Timer
  const [mode, setMode] = useState('stopwatch');
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [timerTarget, setTimerTarget] = useState(300);
  const [timerLeft, setTimerLeft] = useState(300);
  const intervalRef = useRef(null);
  const startRef = useRef(null);
  const savedElapsed = useRef(0);

  const beatsPerMeasure = parseInt(timeSig.split('/')[0]);
  const { currentBeat, isSilentNow } = useMetronome(bpm, timeSig, subdivision, metroOn, rhythmTrainer, soundBars, silentBars, rhythmBarCount, isSilent);

  useEffect(() => { loadExercise(); }, [id]);

  const loadExercise = async () => {
    const isUserEx = id.startsWith('user-');
    const realId = isUserEx ? id.replace('user-', '') : id;

    let ex = null;
    if (isUserEx) {
      const { data } = await supabase.from('user_exercises')
        .select('*, categories(name, color, emoji), subcategories(name)')
        .eq('id', realId).single();
      ex = data;
    } else {
      const { data } = await supabase.from('exercises')
        .select('*, categories(name, color, emoji), subcategories(name)')
        .eq('id', realId).single();
      ex = data;
    }

    if (ex) {
      setExercise(ex);
      if (programSettings?.target_bpm) {
        setBpm(programSettings.target_bpm);
      } else {
        setBpm(ex.default_bpm || 80);
      }
      if (ex.default_time_sig) setTimeSig(ex.default_time_sig);
      if (programSettings?.duration_minutes) {
        const secs = programSettings.duration_minutes * 60;
        setTimerTarget(secs);
        setTimerLeft(secs);
        setMode('timer');
      }
    }

    const isUserEx2 = id.startsWith('user-');
    const realId2 = isUserEx2 ? id.replace('user-', '') : id;
    const sessQuery = supabase.from('sessions').select('*').eq('user_id', user.id).order('completed_at', { ascending: false }).limit(5);
    const { data: sessions } = isUserEx2
      ? await sessQuery.eq('user_exercise_id', parseInt(realId2))
      : await sessQuery.eq('exercise_id', parseInt(realId2));
    if (sessions) setLogs(sessions);

    const isUserEx3 = id.startsWith('user-');
    const realId3 = isUserEx3 ? id.replace('user-', '') : id;
    const { data: noteData } = !isUserEx3
      ? await supabase.from('exercise_notes').select('note').eq('user_id', user.id).eq('exercise_id', parseInt(realId3)).single()
      : { data: null };
    if (noteData) { setSavedNote(noteData.note); setNote(noteData.note); }
  };

  const formatTime = s => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  const saveSession = async (duration) => {
    if (duration < 3) return;
    setSaving(true);
    const isUE = id.startsWith('user-');
    const sessionData = {
      user_id: user.id, bpm, duration_seconds: duration, notes: savedNote,
      ...(isUE ? { user_exercise_id: parseInt(id.replace('user-', '')) } : { exercise_id: parseInt(id) }),
    };
    const { data } = await supabase.from('sessions').insert(sessionData).select().single();
    if (data) setLogs(prev => [data, ...prev]);
    setSaving(false);
  };

  const saveNote = async () => {
    setSavedNote(note);
    setEditingNote(false);
    if (!id.startsWith('user-')) {
      await supabase.from('exercise_notes').upsert({ user_id: user.id, exercise_id: parseInt(id), note, updated_at: new Date().toISOString() }, { onConflict: 'user_id,exercise_id' });
    }
  };

  useEffect(() => {
    if (running) {
      startRef.current = Date.now() - savedElapsed.current * 1000;
      intervalRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - startRef.current) / 1000);
        if (mode === 'stopwatch') { setElapsed(secs); }
        else {
          const left = timerTarget - secs;
          if (left <= 0) { setTimerLeft(0); setRunning(false); setMetroOn(false); setFinished(true); clearInterval(intervalRef.current); setPendingDuration(timerTarget); setSessionNote(''); setShowNoteModal(true); }
          else setTimerLeft(left);
        }
      }, 250);
    } else { clearInterval(intervalRef.current); if (mode === 'stopwatch') savedElapsed.current = elapsed; }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const handleStop = () => {
    const dur = mode === 'stopwatch' ? elapsed : timerTarget - timerLeft;
    setRunning(false); setMetroOn(false);
    if (dur >= 3) {
      setPendingDuration(dur);
      setSessionNote('');
      setShowNoteModal(true);
    }
    setElapsed(0); setTimerLeft(timerTarget); savedElapsed.current = 0; setFinished(false);
  };

  const confirmSaveSession = async () => {
    setSaving(true);
    try {
      const isUE2 = id.startsWith('user-');
      const { data, error } = await supabase.from('sessions').insert({
        user_id: user.id, bpm,
        duration_seconds: pendingDuration,
        notes: sessionNote || null,
        ...(isUE2 ? { user_exercise_id: parseInt(id.replace('user-', '')) } : { exercise_id: parseInt(id) }),
      }).select().single();
      if (error) console.error('Session save error:', error);
      if (data) setLogs(prev => [data, ...prev]);
    } catch (err) {
      console.error('Session save exception:', err);
    }
    setSaving(false);
    setShowNoteModal(false);
    setFinished(false);
    setSessionNote('');
    setPendingDuration(0);
    navigate('/'); // volta ao dashboard após guardar
  };

  const handlePlay = () => {
    if (finished) { setFinished(false); setTimerLeft(timerTarget); setElapsed(0); savedElapsed.current = 0; }
    const wasRunning = running;
    setRunning(r => !r);
    // Start metro on first play, pause metro on pause
    if (!wasRunning) {
      setMetroOn(true);
    } else {
      setMetroOn(false); // pause silences metro
    }
  };

  const switchMode = m => { setMode(m); setRunning(false); setMetroOn(false); setElapsed(0); setTimerLeft(timerTarget); savedElapsed.current = 0; };

  const displayTime = mode === 'stopwatch' ? formatTime(elapsed) : formatTime(timerLeft);
  const circum = 2 * Math.PI * 52;
  const timerProgress = mode === 'timer' ? timerLeft / timerTarget : 1;

  const formatDate = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diff = Math.floor((now - date) / 86400000);
    if (diff === 0) return `Today, ${String(date.getHours()).padStart(2,'0')}:${String(date.getMinutes()).padStart(2,'0')}`;
    if (diff === 1) return 'Yesterday';
    return `${diff} days ago`;
  };

  if (!exercise) return (
    <div style={{ minHeight: '100vh', background: '#111009', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #2A2520', borderTopColor: '#FF5500', animation: 'spin 0.8s linear infinite' }}/>
    </div>
  );

  return (
    <div style={{ fontFamily: "'Barlow', sans-serif", background: '#111009', minHeight: '100vh', color: '#F0EBE3', maxWidth: 'var(--app-width, 860px)', margin: '0 auto', position: 'relative' }}>

      {/* Fullscreen image */}
      {imgExpanded && (
        <div onClick={() => setImgExpanded(false)} style={{ position: 'fixed', inset: 0, zIndex: 999, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <img src={exercise.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
          <div style={{ position: 'absolute', bottom: 32, color: 'rgba(255,255,255,0.4)', fontSize: 13, letterSpacing: 1 }}>toca para fechar</div>

          {/* Timer widget — canto superior direito */}
          {running && (
            <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)', borderRadius: 12, padding: '8px 14px', border: '1px solid rgba(255,255,255,0.12)', minWidth: 90, textAlign: 'center' }}>
              <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 10, fontWeight: 700, letterSpacing: 2, color: 'rgba(255,255,255,0.5)', marginBottom: 2 }}>
                {mode === 'timer' ? 'TIME' : 'ELAPSED'}
              </div>
              <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 28, fontWeight: 800, color: mode === 'timer' && timerLeft < 30 ? '#FF5500' : '#fff', lineHeight: 1 }}>
                {mode === 'timer' ? formatTime(timerLeft) : formatTime(elapsed)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 4 }}>
                <span style={{ fontFamily: "'Barlow Condensed'", fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)' }}>{timeSig}</span>
                <span style={{ fontFamily: "'Barlow Condensed'", fontSize: 12, fontWeight: 700, color: '#FF5500' }}>♩ {bpm}</span>
              </div>
              {mode === 'timer' && (
                <div style={{ marginTop: 5, height: 3, background: 'rgba(255,255,255,0.15)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${((timerTarget - timerLeft) / timerTarget) * 100}%`, background: '#FF5500', borderRadius: 2 }}/>
                </div>
              )}
            </div>
          )}

          {/* Título */}
          <div style={{ position: 'absolute', top: 20, left: 20 }}>
            <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.6)', letterSpacing: 1 }}>
              {exercise?.title}
            </div>
          </div>
        </div>
      )}

      {/* Finished overlay */}
      {finished && !showNoteModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(17,16,9,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', gap: 12 }}>
          <div style={{ fontSize: 80 }}>🥁</div>
          <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 56, fontWeight: 800, color: '#FF5500', letterSpacing: 3 }}>TEMPO!</div>
          <div style={{ fontSize: 13, color: '#7A7268', letterSpacing: 2, fontWeight: 600 }}>SAVING…</div>
        </div>
      )}

      {/* Note modal before saving */}
      {showNoteModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', backdropFilter: 'blur(6px)' }}>
          <div style={{ background: '#171410', borderRadius: '20px 20px 0 0', padding: '28px 20px 44px', width: '100%', maxWidth: 460 }}>
            <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 22, fontWeight: 800, letterSpacing: 2, color: '#F0EBE3', marginBottom: 6 }}>SESSION COMPLETE</div>
            <div style={{ fontSize: 13, color: '#7A7268', marginBottom: 20 }}>
              Duration: <span style={{ color: '#FF5500', fontWeight: 700 }}>{formatTime(pendingDuration)}</span> · BPM: <span style={{ color: '#FF5500', fontWeight: 700 }}>{bpm}</span>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, color: '#7A7268', fontFamily: "'Barlow Condensed'", fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                Session note (optional)
              </label>
              <textarea
                value={sessionNote}
                onChange={e => setSessionNote(e.target.value)}
                placeholder="How did it go? Difficulties, observations…"
                autoFocus
                rows={3}
                style={{ width: '100%', background: '#1C1916', border: '1.5px solid #2E2924', borderRadius: 8, color: '#F0EBE3', fontFamily: "'Barlow', sans-serif", fontSize: 14, padding: '12px 14px', outline: 'none', resize: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setShowNoteModal(false); setFinished(false); setPendingDuration(0); }} style={{ flex: 1, padding: '13px', background: '#2A2520', color: '#9A9088', borderRadius: 10, fontFamily: "'Barlow Condensed'", fontWeight: 700, fontSize: 14, letterSpacing: 1, border: 'none', cursor: 'pointer' }}>
                DISCARD
              </button>
              <button onClick={confirmSaveSession} disabled={saving} style={{ flex: 2, padding: '13px', background: '#FF5500', color: '#fff', borderRadius: 10, fontFamily: "'Barlow Condensed'", fontWeight: 800, fontSize: 14, letterSpacing: 2, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {saving ? <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite' }}/> : 'SAVE SESSION'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ padding: '52px 20px 16px', background: 'linear-gradient(180deg, #0E0B08 0%, #111009 100%)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'transparent', color: '#7A7268', padding: '4px 0', marginTop: 4 }}><BackIcon/></button>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: '#FF5500', marginBottom: 3 }}>
              {exercise.categories?.name} · {exercise.subcategories?.name}
            </div>
            <h1 style={{ fontFamily: "'Barlow Condensed'", fontSize: 32, fontWeight: 800, letterSpacing: 1, lineHeight: 1, color: '#F0EBE3' }}>{exercise.title}</h1>
          </div>
        </div>
      </div>

      <div style={{ padding: '8px 16px 90px' }}>

        {/* Image */}
        {exercise.image_url && (
          <div onClick={() => setImgExpanded(true)} style={{ borderRadius: 12, overflow: 'hidden', height: 190, position: 'relative', cursor: 'zoom-in', marginBottom: 12 }}>
            <img src={exercise.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(17,16,9,0.95) 0%, transparent 55%)' }}/>
            <div style={{ position: 'absolute', bottom: 14, right: 14, background: 'rgba(255,255,255,0.12)', borderRadius: 8, padding: 7, backdropFilter: 'blur(6px)', color: '#fff' }}><ExpandIcon/></div>
          </div>
        )}

        {exercise.description && (
          <div style={{ background: '#1C1916', border: '1px solid #2A2520', borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
            <p style={{ fontSize: 13, color: '#C4B9AC', lineHeight: 1.6 }}>{exercise.description}</p>
          </div>
        )}

        {exercise.youtube_url && (
          <a href={exercise.youtube_url} target="_blank" rel="noopener noreferrer"
            style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, padding:'13px', background:'#FF0000', borderRadius:12, marginBottom:12, textDecoration:'none', color:'#fff' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M23 7s-.3-1.9-1.2-2.7c-1.1-1.2-2.4-1.2-3-1.3C16.2 3 12 3 12 3s-4.2 0-6.8.1c-.6.1-1.9.1-3 1.3C1.3 5.2 1 7 1 7S.7 9.1.7 11.2v2c0 2.1.3 4.2.3 4.2s.3 1.9 1.2 2.7c1.1 1.2 2.6 1.1 3.3 1.2C7.2 21.4 12 21.5 12 21.5s4.2 0 6.8-.2c.6-.1 1.9-.1 3-1.3.9-.8 1.2-2.7 1.2-2.7s.3-2.1.3-4.2v-2C23.3 9.1 23 7 23 7zM9.7 15.5V8.4l8.1 3.6-8.1 3.5z"/></svg>
            <span style={{ fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:15, letterSpacing:1 }}>SEE DEMO</span>
          </a>
        )}

        {/* Metronome */}
        <div className="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span className="section-title" style={{ marginBottom: 0 }}>Metronome</span>
            <button onClick={() => setMetroOn(m => !m)} style={{ background: metroOn ? '#FF5500' : '#332C24', color: metroOn ? '#fff' : '#7A7268', padding: '5px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700, fontFamily: "'Barlow Condensed'", letterSpacing: 1 }}>
              {metroOn ? '▶ PLAYING' : 'START'}
            </button>
          </div>

          {/* Beats */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
            {Array.from({ length: beatsPerMeasure }).map((_, i) => {
              const isActive = metroOn && currentBeat === i;
              return <div key={i} style={{ width: i===0?16:12, height: i===0?16:12, borderRadius: '50%', background: isActive ? (i===0?'#FF5500':'#FF8844') : (i===0?'#3D3328':'#2A2420'), border: i===0?`2px solid ${isActive?'#FF5500':'#4A3E32'}`:'none', boxShadow: isActive&&i===0?'0 0 12px #FF550088':'none', transition: 'all 0.05s' }}/>;
            })}
          </div>

          {/* BPM */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 10 }}>
            <button onClick={() => setBpm(b => Math.max(20, b-1))} style={{ background: '#2E2720', color: '#C4B9AC', width: 44, height: 56, fontSize: 22, fontWeight: 300, borderRadius: '10px 0 0 10px' }}>−</button>
            <div style={{ flex: 1, background: '#2A231C', height: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily: "'Barlow Condensed'", fontSize: 40, fontWeight: 800, color: '#FF5500', lineHeight: 1 }}>{bpm}</span>
              <span style={{ fontSize: 10, color: '#7A7268', fontWeight: 700, letterSpacing: 2 }}>BPM</span>
            </div>
            <button onClick={() => setBpm(b => Math.min(300, b+1))} style={{ background: '#2E2720', color: '#C4B9AC', width: 44, height: 56, fontSize: 22, fontWeight: 300, borderRadius: '0 10px 10px 0' }}>+</button>
          </div>

          <input type="range" min={20} max={300} value={bpm} onChange={e => setBpm(Number(e.target.value))}
            style={{ width: '100%', marginBottom: 14, background: `linear-gradient(to right, #FF5500 ${((bpm-20)/280)*100}%, #2E2720 0%)`, accentColor: '#FF5500' }}/>

          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {[-10,-5,+5,+10].map(d => <button key={d} onClick={() => setBpm(b => Math.min(300,Math.max(20,b+d)))} style={{ flex: 1, padding: '6px 0', background: '#2A231C', color: '#C4B9AC', borderRadius: 8, fontFamily: "'Barlow Condensed'", fontWeight: 700, fontSize: 13 }}>{d>0?`+${d}`:d}</button>)}
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {TIME_SIGS.map(t => <button key={t} onClick={() => setTimeSig(t)} style={{ flex: 1, padding: '6px 0', background: timeSig===t?'#FF5500':'#2A231C', color: timeSig===t?'#fff':'#9A8E82', borderRadius: 7, fontFamily: "'Barlow Condensed'", fontWeight: 700, fontSize: 12 }}>{t}</button>)}
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            {SUBDIVISIONS.map(s => <button key={s.value} onClick={() => setSubdivision(s.value)} style={{ flex: 1, padding: '8px 2px', borderRadius: 8, background: subdivision===s.value?'rgba(255,85,0,0.15)':'#2A231C', border: `1.5px solid ${subdivision===s.value?'#FF5500':'transparent'}`, color: subdivision===s.value?'#FF5500':'#7A7268', fontFamily: "'Barlow Condensed'", fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textAlign: 'center', cursor: 'pointer' }}>
              <div style={{ fontSize: 15, marginBottom: 2 }}>{s.symbol}</div>{s.label}
            </button>)}
          </div>
        </div>

        {/* Rhythm Trainer */}
        <div className="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: rhythmTrainer ? 16 : 0 }}>
            <div>
              <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: rhythmTrainer ? '#FF5500' : '#7A7268', textTransform: 'uppercase' }}>
                Rhythm Trainer
              </div>
              {!rhythmTrainer && <div style={{ fontSize: 11, color: '#4A4038', marginTop: 2 }}>Alternates bars of sound and silence</div>}
            </div>
            <button onClick={() => { setRhythmTrainer(r => !r); rhythmBarCount.current = 0; isSilent.current = false; }}
              style={{ width: 44, height: 24, borderRadius: 12, background: rhythmTrainer ? '#FF5500' : '#2A2520', position: 'relative', border: 'none', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
              <div style={{ position: 'absolute', width: 18, height: 18, borderRadius: '50%', background: '#fff', top: 3, left: rhythmTrainer ? 23 : 3, transition: 'left 0.2s' }}/>
            </button>
          </div>

          {rhythmTrainer && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: '#7A7268', fontFamily: "'Barlow Condensed'", fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8 }}>
                  🔊 Sound bars
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => setSoundBars(b => Math.max(1, b-1))} style={{ background: '#2A231C', color: '#C4B9AC', width: 32, height: 32, borderRadius: 8, fontSize: 18, border: 'none', cursor: 'pointer' }}>−</button>
                  <div style={{ flex: 1, textAlign: 'center', fontFamily: "'Barlow Condensed'", fontSize: 28, fontWeight: 800, color: '#FF5500' }}>{soundBars}</div>
                  <button onClick={() => setSoundBars(b => Math.min(16, b+1))} style={{ background: '#2A231C', color: '#C4B9AC', width: 32, height: 32, borderRadius: 8, fontSize: 18, border: 'none', cursor: 'pointer' }}>+</button>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#7A7268', fontFamily: "'Barlow Condensed'", fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8 }}>
                  🔇 Silent bars
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => setSilentBars(b => Math.max(1, b-1))} style={{ background: '#2A231C', color: '#C4B9AC', width: 32, height: 32, borderRadius: 8, fontSize: 18, border: 'none', cursor: 'pointer' }}>−</button>
                  <div style={{ flex: 1, textAlign: 'center', fontFamily: "'Barlow Condensed'", fontSize: 28, fontWeight: 800, color: '#7A7268' }}>{silentBars}</div>
                  <button onClick={() => setSilentBars(b => Math.min(16, b+1))} style={{ background: '#2A231C', color: '#C4B9AC', width: 32, height: 32, borderRadius: 8, fontSize: 18, border: 'none', cursor: 'pointer' }}>+</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Timer */}
        <div className="section">
          <div style={{ display: 'flex', background: '#2A231C', borderRadius: 10, padding: 4, marginBottom: 16 }}>
            {[['stopwatch','⏱ Stopwatch'],['timer','⏳ Timer']].map(([m,l]) => (
              <button key={m} onClick={() => switchMode(m)} style={{ flex: 1, padding: '9px 8px', background: mode===m?'#FF5500':'transparent', color: mode===m?'#fff':'#7A7268', borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "'Barlow Condensed'", letterSpacing: 1 }}>{l}</button>
            ))}
          </div>

          {mode === 'timer' && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              {[5,10,15,20,30].map(min => <button key={min} onClick={() => { setTimerTarget(min*60); setTimerLeft(min*60); setRunning(false); savedElapsed.current=0; }} style={{ flex: 1, padding: '6px 0', background: timerTarget===min*60?'rgba(255,85,0,0.15)':'#2A231C', border: `1.5px solid ${timerTarget===min*60?'#FF5500':'transparent'}`, color: timerTarget===min*60?'#FF5500':'#9A8E82', borderRadius: 8, fontFamily: "'Barlow Condensed'", fontWeight: 700, fontSize: 12 }}>{min}m</button>)}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            {mode === 'timer' ? (
              <div style={{ position: 'relative', width: 128, height: 128 }}>
                <svg width="128" height="128" style={{ transform: 'rotate(-90deg)', position: 'absolute' }}>
                  <circle cx="64" cy="64" r="52" fill="none" stroke="#2A231C" strokeWidth="6"/>
                  <circle cx="64" cy="64" r="52" fill="none" stroke="#FF5500" strokeWidth="6" strokeDasharray={circum} strokeDashoffset={circum*(1-timerProgress)} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.25s linear' }}/>
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 36, fontWeight: 800, color: timerLeft<10&&running?'#FF5500':'#F0EBE3', lineHeight: 1 }}>{displayTime}</div>
                  <div style={{ fontSize: 10, color: '#7A7268', fontWeight: 700, letterSpacing: 2, marginTop: 2 }}>REMAINING</div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 64, fontWeight: 800, color: '#F0EBE3', letterSpacing: 3, lineHeight: 1 }}>{displayTime}</div>
                <div style={{ fontSize: 10, color: '#7A7268', fontWeight: 700, letterSpacing: 2, marginTop: 4 }}>TEMPO DECORRIDO</div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
            <button onClick={handleStop} disabled={elapsed===0&&timerLeft===timerTarget&&!running} style={{ background: '#2E2720', color: '#9A9088', width: 50, height: 50, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><StopIcon/></button>
            <button onClick={handlePlay} style={{ background: running?'#CC4400':'#FF5500', width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: running?'0 0 32px rgba(255,85,0,0.45)':'0 4px 20px rgba(255,85,0,0.3)' }}>
              {running?<PauseIcon/>:<PlayIcon/>}
            </button>
            <button onClick={() => setMetroOn(m => !m)} style={{ background: metroOn?'rgba(255,85,0,0.15)':'#2E2720', border: `2px solid ${metroOn?'#FF5500':'transparent'}`, color: metroOn?'#FF5500':'#7A7268', width: 50, height: 50, borderRadius: '50%', fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>♩</button>
          </div>

          {(running || saving) && (
            <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: '#FF550077', fontWeight: 700, letterSpacing: 2, fontFamily: "'Barlow Condensed'" }}>
              {saving ? '💾 SAVING…' : '● SAVING SESSION'}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span className="section-title" style={{ marginBottom: 0 }}>Notes</span>
            {!editingNote
              ? <button onClick={() => { setNote(savedNote); setEditingNote(true); }} style={{ background: '#2A231C', color: '#C4B9AC', padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, fontFamily: "'Barlow Condensed'", letterSpacing: 1 }}><PencilIcon/> EDIT</button>
              : <button onClick={saveNote} style={{ background: '#FF5500', color: '#fff', padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "'Barlow Condensed'", letterSpacing: 1 }}>SAVE</button>
            }
          </div>
          {editingNote
            ? <textarea value={note} onChange={e => setNote(e.target.value)} autoFocus rows={3} placeholder="Notas, dificuldades, objetivos…" style={{ width: '100%', background: '#2A231C', border: '1.5px solid #FF5500', borderRadius: 8, color: '#F0EBE3', fontSize: 14, lineHeight: 1.6, padding: '12px 14px', resize: 'none', outline: 'none', fontFamily: "'Barlow', sans-serif" }}/>
            : <p style={{ fontSize: 14, lineHeight: 1.6, color: savedNote?'#C4B9AC':'#4A4038', fontStyle: savedNote?'normal':'italic' }}>{savedNote || 'No notes. Tap EDIT to add.'}</p>
          }
        </div>

        {/* Last sessions + BPM Chart */}
        <div className="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span className="section-title" style={{ marginBottom: 0 }}>Last Sessions</span>
            {logs.length > 2 && <button onClick={() => setShowAllLogs(l => !l)} style={{ background: 'transparent', color: '#FF5500', fontSize: 12, fontWeight: 700, fontFamily: "'Barlow Condensed'", letterSpacing: 1 }}>{showAllLogs?'LESS ↑':'SEE ALL ↓'}</button>}
          </div>

          {/* BPM Chart button */}
          {logs.length >= 2 && (
            <button onClick={() => setShowChart(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: '#1A1510', border: '1px solid #2A231C', borderRadius: 10, padding: '12px 16px', cursor: 'pointer', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>📈</span>
                <span style={{ fontFamily: "'Barlow Condensed'", fontSize: 14, fontWeight: 700, color: '#C4B9AC', letterSpacing: 1 }}>BPM EVOLUTION</span>
              </div>
              <span style={{ fontFamily: "'Barlow Condensed'", fontSize: 12, fontWeight: 700, color: '#FF5500', letterSpacing: 1 }}>VIEW →</span>
            </button>
          )}

          {logs.length === 0
            ? <p style={{ fontSize: 13, color: '#3A3530', fontStyle: 'italic' }}>No sessions yet. Start training!</p>
            : (showAllLogs ? logs : logs.slice(0,2)).map((log, i, arr) => (
              <div key={log.id} style={{ paddingBottom: 12, marginBottom: 12, borderBottom: i<arr.length-1?'1px solid #2A231C':'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#7A7268' }}>{formatDate(log.completed_at)}</span>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <span style={{ fontSize: 13, color: '#FF5500', fontWeight: 700, fontFamily: "'Barlow Condensed'" }}>♩ {log.bpm} BPM</span>
                    <span style={{ fontSize: 13, color: '#C4B9AC', fontFamily: "'Barlow Condensed'" }}>⏱ {String(Math.floor((log.duration_seconds||0)/60)).padStart(2,'0')}:{String((log.duration_seconds||0)%60).padStart(2,'0')}</span>
                  </div>
                </div>
                {log.notes && <p style={{ fontSize: 12, color: '#7A7268', marginTop: 4, fontStyle: 'italic' }}>"{log.notes}"</p>}
              </div>
            ))
          }
        </div>

        {/* Chart Modal */}
        {showChart && logs.length >= 2 && (() => {
          const chartLogs = [...logs].reverse();
          const bpms = chartLogs.map(l => l.bpm).filter(Boolean);
          const dates = chartLogs.map(l => {
            const d = new Date(l.completed_at);
            return `${d.getDate()}/${d.getMonth()+1}`;
          });
          const minBpm = Math.min(...bpms);
          const maxBpm = Math.max(...bpms);
          const W = 500, H = 260, padL = 48, padR = 20, padT = 30, padB = 48;
          const xStep = bpms.length > 1 ? (W - padL - padR) / (bpms.length - 1) : 0;
          const yRange = maxBpm - minBpm || 10;
          const toX = i => padL + i * xStep;
          const toY = v => padT + (1 - (v - minBpm + yRange*0.05) / (yRange * 1.1)) * (H - padT - padB);
          const points = bpms.map((v,i) => `${toX(i)},${toY(v)}`).join(' ');
          const minIdx = bpms.indexOf(minBpm);
          const maxIdx = bpms.indexOf(maxBpm);



          return (
            <div onClick={() => setShowChart(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: '#1A1510', borderRadius: 16, padding: '24px 20px', width: '100%', maxWidth: 580, border: '1px solid #2A231C' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 18, fontWeight: 900, color: '#F0EBE3', letterSpacing: 1 }}>BPM EVOLUTION</div>
                    <div style={{ fontSize: 12, color: '#5A5248' }}>{exercise?.title}</div>
                  </div>
                  <button onClick={() => setShowChart(false)} style={{ background: '#2A231C', color: '#C4B9AC', border: 'none', borderRadius: 8, padding: '8px 14px', fontFamily: "'Barlow Condensed'", fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>✕</button>
                </div>
                <svg id="bpm-chart-svg" width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', background: '#111009', borderRadius: 8 }}>
                  {/* Y axis labels */}
                  {[0,0.25,0.5,0.75,1].map((t,i) => {
                    const val = Math.round(minBpm + t * yRange);
                    const y = toY(val);
                    return (
                      <g key={i}>
                        <line x1={padL} y1={y} x2={W-padR} y2={y} stroke="#2A231C" strokeWidth="1" strokeDasharray="4,4"/>
                        <text x={padL-6} y={y+4} textAnchor="end" fontSize="10" fill="#5A5248">{val}</text>
                      </g>
                    );
                  })}
                  {/* X axis labels */}
                  {dates.map((d,i) => (
                    <text key={i} x={toX(i)} y={H-padB+16} textAnchor="middle" fontSize="9" fill="#5A5248">{d}</text>
                  ))}
                  {/* Y axis title */}
                  <text x={12} y={H/2} textAnchor="middle" fontSize="10" fill="#5A5248" transform={`rotate(-90, 12, ${H/2})`}>BPM</text>
                  {/* Area fill */}
                  <polyline points={`${toX(0)},${H-padB} ${points} ${toX(bpms.length-1)},${H-padB}`} fill="rgba(255,85,0,0.08)" stroke="none"/>
                  {/* Line */}
                  <polyline points={points} fill="none" stroke="#FF5500" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
                  {/* All dots */}
                  {bpms.map((v,i) => (
                    <circle key={i} cx={toX(i)} cy={toY(v)} r="3" fill="#FF5500" opacity="0.7"/>
                  ))}
                  {/* Min */}
                  <circle cx={toX(minIdx)} cy={toY(minBpm)} r="5" fill="#4A9EFF"/>
                  <text x={toX(minIdx)} y={toY(minBpm)+16} textAnchor="middle" fontSize="10" fill="#4A9EFF" fontWeight="700">MIN {minBpm}</text>
                  {/* Max */}
                  <circle cx={toX(maxIdx)} cy={toY(maxBpm)} r="5" fill="#FF5500"/>
                  <text x={toX(maxIdx)} y={toY(maxBpm)-10} textAnchor="middle" fontSize="10" fill="#FF5500" fontWeight="700">MAX {maxBpm}</text>
                  {/* Axes */}
                  <line x1={padL} y1={padT} x2={padL} y2={H-padB} stroke="#3A3530" strokeWidth="1.5"/>
                  <line x1={padL} y1={H-padB} x2={W-padR} y2={H-padB} stroke="#3A3530" strokeWidth="1.5"/>
                </svg>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                  <span style={{ fontSize: 12, color: '#4A9EFF', fontWeight: 700, fontFamily: "'Barlow Condensed'" }}>● MIN: {minBpm} BPM</span>
                  <span style={{ fontSize: 12, color: '#FF5500', fontWeight: 700, fontFamily: "'Barlow Condensed'" }}>● MAX: {maxBpm} BPM</span>
                  <span style={{ fontSize: 12, color: '#7A7268', fontFamily: "'Barlow Condensed'" }}>{bpms.length} SESSIONS</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Add to program */}
        <button onClick={() => navigate('/programs', { state: { exercise: { id: exercise.id, title: exercise.title, image_url: exercise.image_url, default_bpm: exercise.default_bpm } } })} style={{ width: '100%', padding: '16px', background: 'transparent', border: '2px dashed #3A3530', borderRadius: 12, color: '#7A7268', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: "'Barlow Condensed'", fontSize: 15, fontWeight: 700, letterSpacing: 1.5, cursor: 'pointer' }}>
          + ADD TO PROGRAM
        </button>

      </div>
    </div>
  );
}
